// near-fin-transfer — finalize a transfer whose destination is NEAR.
//
// "Unstick a stuck destination tx": the transfer was initiated on the
// origin chain (an `InitTransfer` event) but the relayer never finalised
// it on NEAR. The operator re-finalises it by submitting an origin-chain
// proof to the omni-bridge `fin_transfer` method, signed with their NEAR
// wallet.
//
// The proof mechanism is per-origin-chain (authoritative mapping from
// bridge-cli `omni_connector_command.rs` NearFinTransfer):
//
//   Eth                       → EVM light-client proof  (@omni-bridge/evm.getEvmProof)
//   Arb / Base / Bnb / Pol /  → Wormhole VAA            (Wormholescan → hex)
//     Sol
//   Abs / Strk                → NEAR chain-signatures MPC proof (mpcProof.ts —
//                               hand-built borsh ForeignTxSignPayload, validated
//                               against the contract's golden vectors)
//
// `fin_transfer` takes borsh-serialized args on-chain, so its `args` travel as
// a `Uint8Array` (from buildFinalization for vaa/evmProof, or hand-serialized
// for MPC). near-api-js's `functionCall` passes a Uint8Array through as raw
// bytes (rather than JSON-stringifying), so the NEAR wallet path signs it.

import { ChainKind } from "@omni-bridge/core";
import type { EvmChainKind } from "@omni-bridge/core";
import { createNearBuilder, FinTransferArgsSchema, GAS } from "@omni-bridge/near";
import { ProofKind, type StorageDepositAction } from "@omni-bridge/near";
import { getEvmProof, getInitTransferTopic, parseInitTransferEvent } from "@omni-bridge/evm";

import { listConnectors, nearView } from "@/api/client";
import { chains } from "@/config/chains";
import type { Network } from "@/config/network";
import type { ConnectorInfo, UnsignedNearTx } from "@/types/dto";
import { sdkTxToUnsignedNearTx, type SdkNearUnsignedTransaction } from "@/wallet/sdkTx";
import {
  buildEvmMpcProverArgs,
  buildStarknetMpcProverArgs,
  type EvmLogFields,
  type StarknetLogFields,
} from "@/wallet/mpcProof";

// ─── Origin-chain dispatch tables ─────────────────────────────────────────

/** Wire form of the origin chain selector (PascalCase `ChainKind` name). */
export type FinOriginChainName =
  | "Eth"
  | "Arb"
  | "Base"
  | "Bnb"
  | "Polygon"
  | "Sol"
  | "Abs"
  | "Starknet";

type EvmKey = "eth" | "arb" | "base" | "bnb" | "pol";

/** EVM origins → (chains.ts key, ChainKind enum, OmniAddress prefix). The
 *  light-client vs VAA choice is separate (see PROOF_BY_CHAIN). */
const EVM_ORIGINS: Record<EvmKey, { chainKind: ChainKind; prefix: string; label: string }> = {
  eth: { chainKind: ChainKind.Eth, prefix: "eth", label: "Ethereum" },
  arb: { chainKind: ChainKind.Arb, prefix: "arb", label: "Arbitrum" },
  base: { chainKind: ChainKind.Base, prefix: "base", label: "Base" },
  bnb: { chainKind: ChainKind.Bnb, prefix: "bnb", label: "BNB" },
  pol: { chainKind: ChainKind.Pol, prefix: "pol", label: "Polygon" },
};

/** Map the form's PascalCase origin name → our internal EVM key. */
const ORIGIN_NAME_TO_EVM_KEY: Partial<Record<FinOriginChainName, EvmKey>> = {
  Eth: "eth",
  Arb: "arb",
  Base: "base",
  Bnb: "bnb",
  Polygon: "pol",
};

type ProofMechanism = "evm-light-client" | "wormhole-vaa" | "mpc";

/** Runtime guard: is `s` one of the origin chains this builder supports?
 *  Used by the dispatcher to reject unknown chains as a hard error rather
 *  than letting them fall into the amber MPC "use bridge-cli" path. */
export function isFinOriginChain(s: string): s is FinOriginChainName {
  return s in PROOF_BY_ORIGIN;
}

/** Authoritative per-origin proof mechanism (bridge-cli NearFinTransfer). */
export const PROOF_BY_ORIGIN: Record<FinOriginChainName, ProofMechanism> = {
  Eth: "evm-light-client",
  Arb: "wormhole-vaa",
  Base: "wormhole-vaa",
  Bnb: "wormhole-vaa",
  Polygon: "wormhole-vaa",
  Sol: "wormhole-vaa",
  Abs: "mpc",
  Starknet: "mpc",
};

// ─── Public entry point ───────────────────────────────────────────────────

export interface FinTransferArgs {
  network: Network;
  /** PascalCase origin chain (matches the ChainKind dropdown). */
  originChain: FinOriginChainName;
  /** init_transfer tx hash on the origin chain. */
  txHash: string;
  /** Connected NEAR account that signs + is the fee recipient. */
  signerId: string;
}

export async function buildNearFinTransfer(args: FinTransferArgs): Promise<{ tx: UnsignedNearTx }> {
  const mechanism = PROOF_BY_ORIGIN[args.originChain];
  if (mechanism === "mpc") {
    // Abs / Strk verify via the NEAR chain-signatures (MPC) network signing
    // over a borsh `ForeignTxSignPayload` (origin log + finality). Built
    // client-side here — see mpcProof.ts (byte-validated against the
    // contract's own golden vectors).
    return buildMpcOriginFinTransfer(args);
  }

  const evmKey = ORIGIN_NAME_TO_EVM_KEY[args.originChain];
  if (evmKey) {
    return buildEvmOriginFinTransfer(args, evmKey, mechanism);
  }
  if (args.originChain === "Sol") {
    return buildSolanaOriginFinTransfer(args);
  }
  throw new FinTransferUnsupported(`Unsupported origin chain for fin_transfer: ${args.originChain}`);
}

/** Thrown when a flow genuinely can't be built client-side; the dispatcher
 *  surfaces it as a `needs_bridge_cli` outcome (amber, with the CLI hint). */
export class FinTransferUnsupported extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FinTransferUnsupported";
  }
}

// ─── EVM origins (Eth = light-client proof, L2s = Wormhole VAA) ────────────

async function buildEvmOriginFinTransfer(
  args: FinTransferArgs,
  evmKey: EvmKey,
  mechanism: ProofMechanism,
): Promise<{ tx: UnsignedNearTx }> {
  const { network, txHash, signerId } = args;
  const origin = EVM_ORIGINS[evmKey];
  const evmCfg = chains(network).evm[evmKey];

  // 1. Fetch the receipt + decode the InitTransfer event (same for every
  //    EVM chain — it's the bridge contract's event, not chain-specific).
  const { createPublicClient, http } = await import("viem");
  const client = createPublicClient({ transport: http(evmCfg.rpcUrl) });
  const receipt = await client.getTransactionReceipt({ hash: txHash as `0x${string}` });
  const event = parseInitTransferEvent(receipt.logs);

  // 2. Storage-deposit actions (token-id mapping + storage queries on NEAR).
  //
  // KNOWN LIMITATION (uncommon edge): bridge-sdk-rs's EVM path also calls
  // `apply_fast_transfer_override` — if this transfer was already
  // *fast-finalised* by a relayer (fronting liquidity) but the final
  // `fin_transfer` is still stuck, the contract reroutes recipient + fee to
  // that relayer, so the storage registration should target the relayer,
  // not the original recipient. We don't query `get_fast_transfer_status`
  // here, so for a fast-transferred-then-stuck transfer the storage actions
  // may target the wrong account and `fin_transfer` could fail. The plain
  // (non-fast) path — by far the common "unstick" case — is correct.
  const tokenOmni = `${origin.prefix}:${event.tokenAddress.toLowerCase()}`;
  const storageDepositActions = await computeStorageDepositActions({
    sourceChainName: args.originChain,
    tokenOmniAddress: tokenOmni,
    recipient: event.recipient,
    fee: event.fee,
    nativeFee: event.nativeTokenFee,
    signerId,
  });

  // 3. Proof — Eth has a NEAR light client; the L2s verify via Wormhole.
  const nearBuilder = createNearBuilder({ network });
  let unsigned;
  if (mechanism === "evm-light-client") {
    const proof = await getEvmProof(
      txHash as `0x${string}`,
      getInitTransferTopic(),
      origin.chainKind as unknown as EvmChainKind,
      network,
      evmCfg.rpcUrl,
    );
    unsigned = nearBuilder.buildFinalization({
      sourceChain: origin.chainKind,
      storageDepositActions,
      evmProof: { proof_kind: ProofKind.InitTransfer, proof },
      signerId,
    });
  } else {
    const vaa = await fetchWormholeVaaHex(network, txHash);
    unsigned = nearBuilder.buildFinalization({
      sourceChain: origin.chainKind,
      storageDepositActions,
      vaa,
      signerId,
    });
  }

  const deposit = await computeFinTransferDeposit(storageDepositActions);
  return { tx: toUnsignedNearTx(unsigned, deposit) };
}

// ─── Solana origin (Wormhole VAA) ──────────────────────────────────────────

async function buildSolanaOriginFinTransfer(args: FinTransferArgs): Promise<{ tx: UnsignedNearTx }> {
  const { network, txHash, signerId } = args;

  // Parse the Solana InitTransfer instruction for the storage-deposit
  // fields (token / recipient / fee / native_fee). Mirrors bridge-sdk-rs
  // `solana_bridge_client.get_transfer_event`.
  const event = await parseSolanaInitTransfer(network, txHash);
  const storageDepositActions = await computeStorageDepositActions({
    sourceChainName: "Sol",
    tokenOmniAddress: `sol:${event.token}`,
    recipient: event.recipient,
    fee: event.fee,
    nativeFee: event.nativeFee,
    signerId,
  });

  const vaa = await fetchWormholeVaaHex(network, txHash);
  const nearBuilder = createNearBuilder({ network });
  const unsigned = nearBuilder.buildFinalization({
    sourceChain: ChainKind.Sol,
    storageDepositActions,
    vaa,
    signerId,
  });
  const deposit = await computeFinTransferDeposit(storageDepositActions);
  return { tx: toUnsignedNearTx(unsigned, deposit) };
}

// ─── MPC origins (Abstract = EVM, Starknet) ─────────────────────────────────
//
// No light client / VAA — the NEAR MPC network re-reads the origin tx and
// signs a `ForeignTxSignPayload`. `buildFinalization` has no MPC path, so we
// serialize `MpcVerifyProofArgs` (mpcProof.ts) + the `FinTransferArgs`
// ourselves and hand-build the `fin_transfer` action.

async function buildMpcOriginFinTransfer(args: FinTransferArgs): Promise<{ tx: UnsignedNearTx }> {
  if (args.originChain === "Abs") return buildAbstractFinTransfer(args);
  if (args.originChain === "Starknet") return buildStarknetFinTransfer(args);
  throw new FinTransferUnsupported(`Unsupported MPC origin: ${args.originChain}`);
}

async function buildAbstractFinTransfer(args: FinTransferArgs): Promise<{ tx: UnsignedNearTx }> {
  const { network, txHash, signerId } = args;
  const evmCfg = chains(network).evm.abs;

  const { createPublicClient, http } = await import("viem");
  const client = createPublicClient({ transport: http(evmCfg.rpcUrl) });
  const receipt = await client.getTransactionReceipt({ hash: txHash as `0x${string}` });

  // Find the raw InitTransfer log (topic0 match) for the MPC payload, and
  // decode it for the storage-deposit actions.
  const topic = getInitTransferTopic().toLowerCase();
  const rawLog = receipt.logs.find((l) => (l.topics[0] ?? "").toLowerCase() === topic);
  if (!rawLog) {
    throw new Error(`No InitTransfer log found in Abstract tx ${txHash}.`);
  }
  const event = parseInitTransferEvent(receipt.logs);

  const evmLog: EvmLogFields = {
    logIndex: BigInt(rawLog.logIndex ?? 0),
    transactionIndex: BigInt(rawLog.transactionIndex ?? 0),
    transactionHash: hexToBytes(rawLog.transactionHash ?? txHash, 32),
    blockHash: hexToBytes(rawLog.blockHash ?? "0x", 32),
    blockNumber: BigInt(rawLog.blockNumber ?? 0n),
    address: hexToBytes(rawLog.address, 20),
    data: rawLog.data, // viem keeps the "0x…" prefix, matching sdk-rs
    topics: rawLog.topics.map((t) => hexToBytes(t, 32)),
  };
  const proverArgs = buildEvmMpcProverArgs(hexToBytes(txHash, 32), evmLog);

  const storageDepositActions = await safeStorageActions({
    sourceChainName: "Abs",
    tokenOmniAddress: `abs:${event.tokenAddress.toLowerCase()}`,
    recipient: event.recipient,
    fee: event.fee,
    nativeFee: event.nativeTokenFee,
    signerId,
  });

  return buildMpcFinTransferTx(ChainKind.Abs, storageDepositActions, proverArgs);
}

async function buildStarknetFinTransfer(args: FinTransferArgs): Promise<{ tx: UnsignedNearTx }> {
  const { network, txHash, signerId } = args;
  const strkCfg = chains(network).starknet;

  const { RpcProvider, hash } = await import("starknet");
  const provider = new RpcProvider({ nodeUrl: strkCfg.rpcUrl });
  const receipt = (await provider.getTransactionReceipt(txHash)) as unknown as {
    events?: Array<{ from_address: string; keys: string[]; data: string[] }>;
    block_hash?: string;
    block_number?: number;
  };
  const events = receipt.events ?? [];
  const initSelector = BigInt(hash.getSelectorFromName("InitTransfer"));
  const logIndex = events.findIndex((e) => e.keys.length > 0 && BigInt(e.keys[0]!) === initSelector);
  if (logIndex < 0) {
    throw new Error(`No InitTransfer event found in Starknet tx ${txHash}.`);
  }
  const event = events[logIndex]!;
  if (receipt.block_hash === undefined || receipt.block_number === undefined) {
    throw new Error("Starknet tx is still pending (pre-confirmed) — wait for AcceptedOnL2.");
  }

  const strkLog: StarknetLogFields = {
    logIndex: BigInt(logIndex),
    blockHash: feltToBytes32(receipt.block_hash),
    blockNumber: BigInt(receipt.block_number),
    data: event.data.map(feltToBytes32),
    fromAddress: feltToBytes32(event.from_address),
    keys: event.keys.map(feltToBytes32),
  };
  const proverArgs = buildStarknetMpcProverArgs(feltToBytes32(txHash), strkLog);

  // Decode the InitTransfer event for storage actions (mirror sdk-rs
  // starknet get_transfer_event): keys[2]=token, data[0..3]=amount/fee/
  // native_fee, data[3..]=recipient (Cairo ByteArray).
  if (event.keys.length < 4 || event.data.length < 3) {
    throw new Error("Starknet InitTransfer event has too few keys/data fields.");
  }
  const tokenHex = normalizeFeltHex(event.keys[2]!);
  const fee = feltToBigInt(event.data[1]!);
  const nativeFee = feltToBigInt(event.data[2]!);
  const [recipient] = decodeCairoByteArray(event.data, 3);

  const storageDepositActions = await safeStorageActions({
    sourceChainName: "Starknet",
    tokenOmniAddress: `strk:${tokenHex}`,
    recipient,
    fee,
    nativeFee,
    signerId,
  });

  return buildMpcFinTransferTx(ChainKind.Strk, storageDepositActions, proverArgs);
}

/** Hand-build the `fin_transfer` NEAR tx for an MPC proof: serialize
 *  `FinTransferArgs { chain_kind, storage_deposit_actions, prover_args }`
 *  (borsh, via the SDK's zorsh schema) and wrap it in a FunctionCall. */
async function buildMpcFinTransferTx(
  sourceChain: ChainKind,
  storageDepositActions: StorageDepositAction[],
  proverArgs: Uint8Array,
): Promise<{ tx: UnsignedNearTx }> {
  const omni = await omniBridgeContract();
  const args = FinTransferArgsSchema.serialize({
    chain_kind: sourceChain,
    storage_deposit_actions: storageDepositActions,
    prover_args: proverArgs,
  });
  const deposit = await computeFinTransferDeposit(storageDepositActions);
  const unsigned: SdkNearUnsignedTransaction = {
    receiverId: omni,
    actions: [
      {
        type: "FunctionCall",
        methodName: "fin_transfer",
        args,
        gas: GAS.FIN_TRANSFER,
        deposit,
      },
    ],
  };
  return { tx: sdkTxToUnsignedNearTx(unsigned) };
}

// ─── Wormhole VAA fetch ────────────────────────────────────────────────────

/** Fetch the signed VAA for `txHash` from Wormholescan and return it as a
 *  hex string. The contract's wormhole prover does `hex::decode(vaa)`, and
 *  the API returns base64 — so we base64-decode then hex-encode (mirrors
 *  bridge-sdk-rs `wormhole_bridge_client.get_vaa_by_tx_hash`). */
async function fetchWormholeVaaHex(network: Network, txHash: string): Promise<string> {
  const host = network === "mainnet" ? "https://api.wormholescan.io" : "https://api.testnet.wormholescan.io";
  const url = `${host}/api/v1/vaas/?txHash=${encodeURIComponent(txHash)}`;
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`Wormholescan returned ${resp.status} for ${txHash} — the VAA may not be signed yet (wait for guardian quorum) or the tx hash is wrong.`);
  }
  const body = (await resp.json()) as { data?: Array<{ vaa?: string }> };
  const vaaB64 = body.data?.[0]?.vaa;
  if (!vaaB64) {
    throw new Error(`No VAA found on Wormholescan for ${txHash} yet — guardians may still be signing; retry shortly.`);
  }
  return base64ToHex(vaaB64);
}

export function base64ToHex(b64: string): string {
  const bin = atob(b64);
  let hex = "";
  for (let i = 0; i < bin.length; i++) {
    hex += bin.charCodeAt(i).toString(16).padStart(2, "0");
  }
  return hex;
}

// ─── byte / felt helpers (MPC paths) ─────────────────────────────────────────

/** 0x-hex → exactly `len` big-endian bytes (left zero-padded / high-truncated). */
function hexToBytes(hex: string, len: number): Uint8Array {
  const clean = hex.replace(/^0x/i, "").padStart(len * 2, "0").slice(-len * 2);
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) out[i] = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function bigIntToBytes(v: bigint, len: number): Uint8Array {
  const out = new Uint8Array(len);
  let x = v;
  for (let i = len - 1; i >= 0; i--) {
    out[i] = Number(x & 0xffn);
    x >>= 8n;
  }
  return out;
}

/** A Starknet felt (0x-hex or decimal string) → 32-byte big-endian. */
function feltToBytes32(felt: string): Uint8Array {
  return bigIntToBytes(BigInt(felt), 32);
}
function feltToBigInt(felt: string): bigint {
  return BigInt(felt);
}
/** Felt → `0x`-prefixed, 64-char zero-padded hex (matches the H256 `to_bytes_be`
 *  form the contract's `OmniAddress::Strk` is built from). */
function normalizeFeltHex(felt: string): string {
  return "0x" + BigInt(felt).toString(16).padStart(64, "0");
}

/** Decode a Cairo `ByteArray` (string) starting at `data[offset]`. Mirrors
 *  bridge-sdk-rs `decode_byte_array`: `num_full_words` × 31-byte words (the
 *  high byte of each 32-byte felt is dropped) + a pending word of
 *  `pending_len` bytes. Returns the UTF-8 string and the next index. */
function decodeCairoByteArray(data: string[], offset: number): [string, number] {
  const numFullWords = Number(feltToBigInt(data[offset]!));
  let idx = offset + 1;
  const bytes: number[] = [];
  for (let i = 0; i < numFullWords; i++) {
    const word = feltToBytes32(data[idx]!);
    for (let j = 1; j < 32; j++) bytes.push(word[j]!); // skip high byte → 31 bytes
    idx++;
  }
  const pendingWord = data[idx]!;
  const pendingLen = Number(feltToBigInt(data[idx + 1]!));
  idx += 2;
  if (pendingLen > 0) {
    const pw = feltToBytes32(pendingWord);
    for (let j = 32 - pendingLen; j < 32; j++) bytes.push(pw[j]!);
  }
  return [new TextDecoder().decode(Uint8Array.from(bytes)), idx];
}

/** Best-effort storage-deposit derivation for the MPC paths: the proof is
 *  the must-have, and the storage actions are an optional recipient/fee
 *  registration. If a view fails (e.g. an unmapped token OmniAddress
 *  format), proceed with no actions rather than blocking the whole
 *  finalization — fin_transfer still succeeds when the accounts are already
 *  registered (the common unstick case). */
async function safeStorageActions(inp: StorageActionInputs): Promise<StorageDepositAction[]> {
  try {
    return await computeStorageDepositActions(inp);
  } catch (e) {
    console.warn("fin_transfer: storage-deposit derivation failed, sending none", e);
    return [];
  }
}

// ─── Storage-deposit actions ───────────────────────────────────────────────

interface StorageActionInputs {
  /** PascalCase ChainKind name for `get_native_token_id`. */
  sourceChainName: FinOriginChainName;
  /** OmniAddress of the origin token (`eth:0x..` / `sol:..`). */
  tokenOmniAddress: string;
  /** OmniAddress recipient from the event (`near:alice.near` etc.). */
  recipient: string;
  fee: bigint;
  nativeFee: bigint;
  signerId: string;
}

/** Mirror of bridge-sdk-rs `get_storage_deposit_actions`: register the
 *  recipient for the bridged token, and the fee recipient (the operator /
 *  signer) for the token and/or native token when a fee is charged. */
async function computeStorageDepositActions(
  inp: StorageActionInputs,
): Promise<StorageDepositAction[]> {
  const omniBridge = await omniBridgeContract();
  const actions: StorageDepositAction[] = [];

  // Recipient is registered only when the destination is a NEAR account.
  const nearRecipient = omniToNearAccount(inp.recipient);
  if (nearRecipient) {
    const tokenId = await nearView<string>(omniBridge, "get_token_id", { address: inp.tokenOmniAddress });
    actions.push(await storageAction(tokenId, nearRecipient));
  }

  if (inp.fee > 0n) {
    const tokenId = await nearView<string>(omniBridge, "get_token_id", { address: inp.tokenOmniAddress });
    actions.push(await storageAction(tokenId, inp.signerId));
  }

  if (inp.nativeFee > 0n) {
    const nativeTokenId = await nearView<string>(omniBridge, "get_native_token_id", {
      chain: chainKindWireName(inp.sourceChainName),
    });
    actions.push(await storageAction(nativeTokenId, inp.signerId));
  }

  // The contract caps storage_deposit_actions at 3.
  return actions.slice(0, 3);
}

interface StorageBalanceBounds {
  min: string;
  max: string | null;
}
interface StorageBalance {
  total: string;
  available: string;
}

/** Build one StorageDepositAction, querying how much (if any) deposit the
 *  account still needs to be registered on the token (NEP-145). */
async function storageAction(tokenId: string, accountId: string): Promise<StorageDepositAction> {
  const bounds = await nearView<StorageBalanceBounds>(tokenId, "storage_balance_bounds", {});
  const current = await nearView<StorageBalance | null>(tokenId, "storage_balance_of", {
    account_id: accountId,
  });
  const min = BigInt(bounds.min);
  const total = current ? BigInt(current.total) : 0n;
  const needed = min > total ? min - total : 0n;
  return {
    token_id: tokenId,
    account_id: accountId,
    storage_deposit_amount: needed > 0n ? needed : null,
  };
}

/** fin_transfer is #[payable]; attach the contract's base requirement plus
 *  any storage registrations we're funding. */
async function computeFinTransferDeposit(actions: StorageDepositAction[]): Promise<bigint> {
  const omniBridge = await omniBridgeContract();
  const base = await nearView<string | number>(omniBridge, "required_balance_for_fin_transfer", {});
  let deposit = BigInt(typeof base === "number" ? Math.trunc(base) : base);
  for (const a of actions) {
    if (a.storage_deposit_amount) deposit += BigInt(a.storage_deposit_amount);
  }
  return deposit;
}

// ─── Solana InitTransfer parse ─────────────────────────────────────────────

interface SolanaTransferEvent {
  token: string;
  recipient: string;
  fee: bigint;
  nativeFee: bigint;
}

// Anchor discriminators + account-key indices, mirroring bridge-sdk-rs
// `solana_bridge_client.rs`. `InitTransfer` carries a token account at
// index 1; `InitTransferSol` (native SOL) has no token (→ system program).
const INIT_TRANSFER_DISCRIMINATOR = Uint8Array.from([174, 50, 134, 99, 122, 243, 243, 224]);
const INIT_TRANSFER_SOL_DISCRIMINATOR = Uint8Array.from([124, 167, 164, 191, 81, 140, 108, 30]);
const INIT_TRANSFER_TOKEN_INDEX = 1;
const SYSTEM_PROGRAM_ID = "11111111111111111111111111111111";

/** Decode the Solana `init_transfer` instruction for the storage-deposit
 *  fields (token / recipient / fee / native_fee). Mirrors bridge-sdk-rs
 *  `solana_bridge_client.get_transfer_event`: scan top-level instructions
 *  for the InitTransfer discriminator, then borsh-decode the payload
 *  (`amount: u128, recipient: string, fee: u128, native_fee: u64, …`). */
async function parseSolanaInitTransfer(network: Network, txHash: string): Promise<SolanaTransferEvent> {
  const cfg = chains(network).solana;
  const { Connection } = await import("@solana/web3.js");
  const conn = new Connection(cfg.rpcUrl, "confirmed");
  const tx = await conn.getTransaction(txHash, {
    commitment: "confirmed",
    maxSupportedTransactionVersion: 0,
  });
  if (!tx) {
    throw new Error(`Solana tx ${txHash} not found on ${cfg.rpcUrl} (wrong signature or network?).`);
  }
  const message = tx.transaction.message;
  const keys = message.getAccountKeys({
    accountKeysFromLookups: tx.meta?.loadedAddresses ?? undefined,
  });

  for (const ix of message.compiledInstructions) {
    const data = ix.data instanceof Uint8Array ? ix.data : new Uint8Array(ix.data);
    if (data.length < 8) continue;
    const disc = data.subarray(0, 8);
    const isInit = bytesEqual(disc, INIT_TRANSFER_DISCRIMINATOR);
    const isInitSol = bytesEqual(disc, INIT_TRANSFER_SOL_DISCRIMINATOR);
    if (!isInit && !isInitSol) continue;

    const payload = decodeInitTransferPayload(data.subarray(8));
    let token = SYSTEM_PROGRAM_ID;
    if (isInit) {
      const tokenKeyIdx = ix.accountKeyIndexes[INIT_TRANSFER_TOKEN_INDEX];
      const tokenKey = tokenKeyIdx === undefined ? undefined : keys.get(tokenKeyIdx);
      if (!tokenKey) throw new Error("Solana InitTransfer: token account index out of range.");
      token = tokenKey.toBase58();
    }
    return {
      token,
      recipient: payload.recipient,
      fee: payload.fee,
      nativeFee: payload.nativeFee,
    };
  }
  throw new Error(
    `No InitTransfer instruction found in Solana tx ${txHash} — is this the init_transfer tx?`,
  );
}

/** Borsh layout of `InitTransferPayload` (the bytes after the 8-byte
 *  discriminator): amount u128, recipient string, fee u128, native_fee u64,
 *  message string. We only need recipient / fee / native_fee. Exported for
 *  unit tests — the byte parsing is the part we can't exercise live. */
export function decodeInitTransferPayload(buf: Uint8Array): {
  recipient: string;
  fee: bigint;
  nativeFee: bigint;
} {
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let off = 0;
  const readU = (bytes: number): bigint => {
    let v = 0n;
    for (let i = 0; i < bytes; i++) v |= BigInt(dv.getUint8(off + i)) << BigInt(8 * i);
    off += bytes;
    return v;
  };
  const readString = (): string => {
    const len = Number(readU(4));
    const slice = buf.subarray(off, off + len);
    off += len;
    return new TextDecoder().decode(slice);
  };
  readU(16); // amount (u128) — not needed for storage actions
  const recipient = readString();
  const fee = readU(16); // u128
  const nativeFee = readU(8); // u64
  return { recipient, fee, nativeFee };
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

let connectorCache: Promise<ConnectorInfo[]> | null = null;
export async function omniBridgeContract(): Promise<string> {
  if (!connectorCache) {
    connectorCache = listConnectors().catch((e) => {
      connectorCache = null;
      throw e;
    });
  }
  const list = await connectorCache;
  const omni = list.find((c) => c.id === "omni-bridge");
  if (!omni) throw new Error("Backend returned no `omni-bridge` connector — check config/connectors.toml.");
  return omni.contract;
}

/** `near:alice.near` → `alice.near`; anything else → null (recipient is on
 *  another chain, no NEAR storage registration needed). */
export function omniToNearAccount(recipient: string): string | null {
  const i = recipient.indexOf(":");
  if (i <= 0) return null;
  const prefix = recipient.slice(0, i).toLowerCase();
  return prefix === "near" ? recipient.slice(i + 1) : null;
}

/** `get_native_token_id` takes a ChainKind whose JSON form is the contract's
 *  variant name. The form's `Polygon`/`Starknet` map to the contract's
 *  `Pol`/`Strk`; the rest are identical. */
export function chainKindWireName(name: FinOriginChainName): string {
  if (name === "Polygon") return "Pol";
  if (name === "Starknet") return "Strk";
  return name;
}

/** Local alias: fin_transfer always overrides the SDK's ONE_YOCTO deposit
 *  placeholder with the storage-inclusive amount (H3). */
function toUnsignedNearTx(
  unsigned: SdkNearUnsignedTransaction,
  depositOverride: bigint,
): UnsignedNearTx {
  return sdkTxToUnsignedNearTx(unsigned, depositOverride);
}
